import { useEffect, useState } from "react";
import axios from "axios";
import { Plus } from "lucide-react";
import { ALL_CASHIER_MODULES, API, DEFAULT_CASHIER_MODULES, fmtErr } from "../lib/pos";
import { useAuth } from "../context/AuthContext";

export function UserAdmin() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [reveal, setReveal] = useState(null); // {email, password}
  const emptyForm = { email: "", password: "", name: "", role: "cashier", modules: [...DEFAULT_CASHIER_MODULES] };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/users`);
      setUsers(r.data);
    } catch (e) { alert(fmtErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (u) => {
    setEditing(u.id);
    setForm({
      email: u.email,
      password: "",
      name: u.name || "",
      role: u.role,
      modules: u.role === "cashier" ? (u.modules || DEFAULT_CASHIER_MODULES) : ALL_CASHIER_MODULES.map(([k]) => k),
    });
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditing(null); setForm(emptyForm); };

  const save = async () => {
    try {
      if (editing) {
        const payload = { name: form.name.trim(), role: form.role };
        if (form.role === "cashier") payload.modules = form.modules;
        await axios.patch(`${API}/users/${editing}`, payload);
      } else {
        if (!form.email.trim() || !form.password) { alert("Email and password are required."); return; }
        const payload = {
          email: form.email.trim(),
          password: form.password,
          name: form.name.trim(),
          role: form.role,
          modules: form.role === "cashier" ? form.modules : [],
        };
        await axios.post(`${API}/users`, payload);
      }
      closeForm();
      load();
    } catch (e) { alert(fmtErr(e)); }
  };

  const remove = async (u) => {
    if (u.email === "manager@kitchenpass.co") { alert("Default admin cannot be deleted."); return; }
    if (u.id === me.id) { alert("You cannot delete your own account while logged in."); return; }
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try { await axios.delete(`${API}/users/${u.id}`); load(); }
    catch (e) { alert(fmtErr(e)); }
  };
  const resetPw = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}?`)) return;
    try {
      const r = await axios.post(`${API}/users/${u.id}/reset-password`);
      setReveal({ email: r.data.email, password: r.data.new_password });
    } catch (e) { alert(fmtErr(e)); }
  };

  const toggleModule = (m) => setForm((f) => ({
    ...f,
    modules: f.modules.includes(m) ? f.modules.filter((x) => x !== m) : [...f.modules, m],
  }));

  return (
    <div className="workspace">
      <div className="page-head">
        <div>
          <p className="eyebrow">MANAGER TOOLS / TEAM</p>
          <h1>User management</h1>
          <p className="muted">Create manager or cashier accounts. Cashiers get a curated set of modules — POS, Kitchen and Daily Summary by default.</p>
        </div>
        <button data-testid="add-user-button" className="primary" onClick={openNew}><Plus size={17} />New user</button>
      </div>

      {loading ? <p className="muted">Loading…</p> : (
        <div className="menu-table user-table">
          <div className="table-head"><span>USER</span><span>ROLE</span><span>MODULES</span><span>ACTIONS</span></div>
          {users.map((u) => (
            <div className="menu-line" key={u.id} data-testid={`user-row-${u.id}`}>
              <div>
                <b>{u.name || "—"}</b>
                <small>{u.email}</small>
              </div>
              <span><b className={u.role === "manager" ? "badge badge-mgr" : "badge badge-cash"}>{u.role.toUpperCase()}</b></span>
              <span style={{ fontSize: 11, color: "var(--muted)" }}>
                {u.role === "manager"
                  ? "Full access"
                  : (u.modules || []).map((m) => (ALL_CASHIER_MODULES.find(([k]) => k === m) || [m, m])[1]).join(" · ") || "—"}
              </span>
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                <button data-testid={`edit-user-${u.id}-button`} className="outline" onClick={() => openEdit(u)}>Edit</button>
                <button data-testid={`reset-user-${u.id}-button`} className="outline" onClick={() => resetPw(u)}>Reset PW</button>
                <button data-testid={`delete-user-${u.id}-button`} className="outline" onClick={() => remove(u)} disabled={u.email === "manager@kitchenpass.co" || u.id === me.id}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="modal-backdrop" onClick={(e) => e.target.classList.contains("modal-backdrop") && closeForm()}>
          <div className="modal" style={{ width: "min(520px, 94vw)", maxHeight: "88vh", overflowY: "auto" }}>
            <div className="section-line">
              <h2>{editing ? "Edit user" : "New user"}</h2>
              <button data-testid="close-user-modal-button" onClick={closeForm}>×</button>
            </div>
            {!editing && (
              <>
                <label className="field">Email<input data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
                <label className="field">Temporary password<input data-testid="user-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min 6 characters" /></label>
              </>
            )}
            <label className="field">Full name<input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label className="field">Role
              <select data-testid="user-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value, modules: e.target.value === "cashier" ? (form.modules.length ? form.modules : DEFAULT_CASHIER_MODULES) : ALL_CASHIER_MODULES.map(([k]) => k) })}>
                <option value="cashier">Cashier</option>
                <option value="manager">Manager (full access)</option>
              </select>
            </label>

            {form.role === "cashier" && (
              <>
                <p className="eyebrow" style={{ marginTop: 14 }}>MODULES <span style={{ color: "var(--muted)", letterSpacing: 0, textTransform: "none", fontSize: 10 }}>· tick what this cashier can open</span></p>
                <div className="module-grid" data-testid="module-grid">
                  {ALL_CASHIER_MODULES.map(([key, label]) => {
                    const on = form.modules.includes(key);
                    const isDefault = DEFAULT_CASHIER_MODULES.includes(key);
                    return (
                      <label key={key} className={`module-chip ${on ? "on" : ""}`} data-testid={`module-${key}-chip`}>
                        <input data-testid={`module-${key}-checkbox`} type="checkbox" checked={on} onChange={() => toggleModule(key)} />
                        <span>{label}{isDefault && <em> · default</em>}</span>
                      </label>
                    );
                  })}
                </div>
              </>
            )}
            {form.role === "manager" && (
              <p className="muted" style={{ fontSize: 11, marginTop: 12 }}>Managers get access to every module — POS, Kitchen, all admin panels, User Management, Reports.</p>
            )}

            <button data-testid="save-user-button" className="primary wide" style={{ marginTop: 18 }} onClick={save}>{editing ? "Save changes" : "Create user"}</button>
          </div>
        </div>
      )}

      {reveal && (
        <div className="modal-backdrop" onClick={(e) => e.target.classList.contains("modal-backdrop") && setReveal(null)}>
          <div className="modal" style={{ width: "min(440px, 94vw)" }} data-testid="reset-password-modal">
            <div className="section-line"><h2>New password</h2><button onClick={() => setReveal(null)}>×</button></div>
            <p className="muted" style={{ fontSize: 12 }}>Share this password with <b>{reveal.email}</b>. It won&apos;t be shown again.</p>
            <div className="pw-reveal" data-testid="reveal-password">{reveal.password}</div>
            <button className="primary wide" style={{ marginTop: 14 }} onClick={() => {
              try { navigator.clipboard?.writeText(reveal.password)?.catch(() => {}); } catch (_) { /* clipboard unavailable in sandboxed iframes */ }
              setReveal(null);
            }}>
              Copy &amp; close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


/* ---------- Completed / Paid orders ---------- */
